<template>
  <v-dialog
    v-model="value"
    max-width="900px"
    persistent
  >
    <v-card :loading="loading" :disabled="loading">
      <v-card-title class="justify-center">
        <span class="text-h5 sp">
          {{ $t('diagnostics.edit_question') }} #{{ question.orden }}
          - {{ countryFlag }} {{ countryCode }}
        </span>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-row>
              <v-col cols="12">
                <v-divider class="mb-4" />
                <h3 class="text-subtitle-1 font-weight-bold mb-3">
                  {{ $t('diagnostics.question').toUpperCase() }}
                </h3>
              </v-col>
            </v-row>

            <v-row v-if="!isGeneralVersion">
              <v-col cols="12">
                <div class="grey--text text-caption mb-1">
                  {{ $t('diagnostics.original_reference') }}:
                </div>
                <v-textarea
                  :value="getOriginalQuestionContent()"
                  readonly
                  outlined
                  dense
                  auto-grow
                  rows="2"
                  class="grey lighten-4"
                  disabled
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-textarea
                  v-model="form.contenido"
                  :label="`${$t('diagnostics.adapted_question')} *`"
                  :rules="[rules.required]"
                  outlined
                  dense
                  auto-grow
                  rows="2"
                  required
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <template v-if="isGeneralVersion">
                  <v-select
                    v-model="form.lbAimOfassessmentId"
                    :items="aimOptions"
                    item-text="label"
                    item-value="id"
                    :label="$t('diagnostics.skill')"
                    :rules="[rules.required]"
                    outlined
                    dense
                    :loading="loadingAims"
                    :disabled="loading || loadingAims"
                    required
                  />
                </template>
                <template v-else>
                  <div class="text-caption grey--text mb-2">{{ $t('diagnostics.skill') }}</div>
                  <v-chip
                    small
                    :color="getSkillColor(question.skill)"
                    text-color="white"
                    class="font-weight-medium"
                  >
                    {{ question.skill || 'N/A' }}
                  </v-chip>
                </template>
              </v-col>
              <v-col cols="6">
                <template v-if="isGeneralVersion">
                  <v-select
                    v-model="form.respuestaCorrecta"
                    :items="answerOptions"
                    item-text="label"
                    item-value="item"
                    :label="$t('diagnostics.correct_answer')"
                    :rules="[rules.required]"
                    outlined
                    dense
                    :disabled="loading"
                    required
                  />
                </template>
                <template v-else>
                  <v-text-field
                    :value="`${getAnswerLetterByItem(question.respuestaCorrecta)}`"
                    :label="$t('diagnostics.correct_answer')"
                    outlined
                    dense
                    readonly
                    disabled
                  />
                </template>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-divider class="mb-4 mt-4" />
                <h3 class="text-subtitle-1 font-weight-bold mb-3">
                  {{ $t('diagnostics.alternatives').toUpperCase() }}
                </h3>
              </v-col>
            </v-row>

            <div v-for="(answer, index) in form.respuestas" :key="index">
              <v-row>
                <v-col cols="12">
                  <div class="d-flex align-center answer-letter-row">
                    <v-chip
                      small
                      :color="isCorrectAnswer(answer.item) ? 'green' : 'grey lighten-2'"
                      :text-color="isCorrectAnswer(answer.item) ? 'white' : 'grey darken-2'"
                      class="mr-2"
                    >
                      {{ getAnswerLetterByItem(answer.item) }}
                    </v-chip>
                    <span
                      v-if="isCorrectAnswer(answer.item)"
                      class="green--text text-caption"
                    >
                      ({{ $t('diagnostics.correct') }})
                    </span>
                  </div>

                  <template v-if="!isGeneralVersion">
                    <div class="grey--text text-caption mb-1">
                      {{ $t('diagnostics.original_reference') }}:
                    </div>
                    <v-textarea
                      :value="getOriginalAnswer(answer.item, index)"
                      readonly
                      outlined
                      dense
                      auto-grow
                      rows="1"
                      class="grey lighten-4 mb-2"
                      disabled
                    />
                  </template>

                  <v-textarea
                    v-model="answer.contenido"
                    :label="
                      `${getAnswerFieldLabel()} ${getAnswerLetterByItem(answer.item)}`
                    "
                    :rules="[rules.required]"
                    outlined
                    dense
                    auto-grow
                    rows="1"
                  />
                </v-col>
              </v-row>
            </div>

          </v-form>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn
          color="grey"
          text
          @click="close"
          :disabled="loading"
        >
          {{ $t('diagnostics.cancel') }}
        </v-btn>
        <v-btn
          color="deep-purple"
          text
          @click="save"
          :disabled="!valid || loading"
          :loading="loading"
        >
          {{ loading ? $t('diagnostics.saving') : $t('diagnostics.save_changes') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getAnswerLetterByItem, getCountryByCode } from '@/constants/diagnostics';
import diagnosticCountryService from '@/services/diagnosticCountry.service';
import snackbarMixin from '@/mixins/snackbar';

export default {
  name: 'EditQuestionVariantModal',
  mixins: [snackbarMixin],
  props: {
    value: { type: Boolean, default: false },
    question: { type: Object, required: true },
    originalQuestion: { type: Object, default: null },
    variant: { type: Object, default: null },
    countryCode: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      valid: false,
      form: {
        contenido: '',
        respuestas: [],
        lbAimOfassessmentId: null,
        respuestaCorrecta: null,
      },
      loadingAims: false,
      aimOptions: [],
      rules: {
        required: (value) => !!value || this.$t('diagnostics.field_required'),
      },
    };
  },
  computed: {
    countryFlag() {
      const country = getCountryByCode(this.countryCode);
      if (this.countryCode === 'GENERAL') {
        return '🌐';
      }
      return country ? country.flag : '';
    },
    isGeneralVersion() {
      return this.countryCode === 'GENERAL';
    },
    answerOptions() {
      return this.form.respuestas.map((answer) => ({
        item: answer.item,
        label: `${this.getAnswerLetterByItem(answer.item)} - ${answer.contenido || ''}`,
      }));
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.initializeForm();
          this.loadAimOptions();
        }
      },
    },
    variant: {
      immediate: true,
      handler() {
        if (this.value) {
          this.initializeForm();
        }
      },
    },
  },
  methods: {
    buildEmptyRespuestas() {
      return [
        { item: 1, contenido: '' },
        { item: 2, contenido: '' },
        { item: 3, contenido: '' },
        { item: 4, contenido: '' },
      ];
    },

    mapRespuestas(respuestas) {
      if (!Array.isArray(respuestas) || respuestas.length === 0) {
        return this.buildEmptyRespuestas();
      }

      return respuestas.map((respuesta, index) => ({
        item: respuesta.item ?? (index + 1),
        contenido: respuesta.contenido || '',
      }));
    },

    async loadAimOptions() {
      if (!this.isGeneralVersion || this.aimOptions.length > 0 || this.loadingAims) {
        return;
      }

      this.loadingAims = true;
      try {
        const { data } = await diagnosticCountryService.getAimOfAssessments();
        let aims = [];
        if (Array.isArray(data?.content)) {
          aims = data.content;
        } else if (Array.isArray(data)) {
          aims = data;
        }

        this.aimOptions = aims.map((aim) => ({
          id: aim.id,
          label: `${(aim.code || '').replace('.', ' ')}${aim.detail ? ` - ${aim.detail}` : ''}`,
        }));
      } catch (_error) {
        this.snackbarError(this.$t('diagnostics.save_error'));
      } finally {
        this.loadingAims = false;
      }
    },

    getAnswerLetterByItem(item) {
      return getAnswerLetterByItem(item || 1);
    },

    isCorrectAnswer(item) {
      const correctAnswer = this.isGeneralVersion
        ? this.form.respuestaCorrecta
        : this.question.respuestaCorrecta;
      return item === correctAnswer;
    },

    getAnswerFieldLabel() {
      return this.isGeneralVersion
        ? this.$t('diagnostics.answer')
        : this.$t('diagnostics.alternative');
    },

    getOriginalAnswer(item, fallbackIndex) {
      const sourceQuestion = this.originalQuestion || this.question;
      if (!sourceQuestion?.respuestas || sourceQuestion.respuestas.length === 0) {
        return '';
      }

      const byItem = sourceQuestion.respuestas.find((respuesta) => respuesta.item === item);
      if (byItem) {
        return byItem.contenido || '';
      }

      const byIndex = sourceQuestion.respuestas[fallbackIndex];
      return byIndex ? (byIndex.contenido || '') : '';
    },

    getOriginalQuestionContent() {
      return this.originalQuestion?.contenido || this.question?.contenido || '';
    },

    getSkillColor(skill) {
      if (!skill) {
        return 'grey';
      }

      const skillPrefix = String(skill).split(' ')[0].toUpperCase();

      if (skillPrefix === 'LOC') {
        return 'teal';
      }

      if (skillPrefix === 'RV') {
        return 'indigo';
      }

      if (skillPrefix === 'IN') {
        return 'deep-orange';
      }

      return 'grey';
    },

    initializeForm() {
      if (this.variant) {
        this.form.contenido = this.variant.contenido || '';
        this.form.respuestas = this.mapRespuestas(this.variant.respuestas);
      } else {
        this.form.contenido = this.question.contenido || '';
        this.form.respuestas = this.mapRespuestas(this.question.respuestas);
      }

      this.form.lbAimOfassessmentId = this.question.lbAimOfassessmentId ?? null;
      this.form.respuestaCorrecta = this.question.respuestaCorrecta ?? null;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    async save() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        try {
          const data = {
            preguntaId: this.question.id,
            countryCode: this.countryCode,
            contenido: this.form.contenido,
            respuestas: this.form.respuestas,
          };

          if (this.isGeneralVersion) {
            await diagnosticCountryService.updateOriginalQuestion(this.question.id, {
              contenido: this.form.contenido,
              respuestas: this.form.respuestas,
              lbAimOfassessmentId: this.form.lbAimOfassessmentId,
              respuestaCorrecta: this.form.respuestaCorrecta,
            });
          } else if (this.variant && this.variant.id) {
            await diagnosticCountryService.updateVariant(this.variant.id, {
              contenido: this.form.contenido,
              respuestas: this.form.respuestas,
            });
          } else {
            await diagnosticCountryService.createVariant(data);
          }

          this.snackbarSuccess(this.$t('diagnostics.save_success'));
          this.$emit('saved');
          this.close();
        } catch (error) {
          const errorMessage = this.getErrorMessage(error);
          this.snackbarError(errorMessage);
        } finally {
          this.loading = false;
        }
      }
    },

    close() {
      this.resetForm();
      this.$emit('input', false);
    },

    resetForm() {
      this.form = {
        contenido: '',
        respuestas: this.buildEmptyRespuestas(),
        lbAimOfassessmentId: null,
        respuestaCorrecta: null,
      };
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    getErrorMessage(error) {
      if (error.response && error.response.data) {
        const { message, title } = error.response.data;
        return message || title || this.$t('diagnostics.save_error');
      }
      return error.message || this.$t('diagnostics.save_error');
    },
  },
  created() {
    this.form.respuestas = this.buildEmptyRespuestas();
  },
};
</script>

<style scoped>
span.sp {
  font-family: "Space Grotesk" !important;
}

.answer-letter-row {
  margin-bottom: 10px;
}

.grey.lighten-4 {
  background-color: #f5f5f5;
}

.v-text-field--disabled,
.v-textarea--disabled {
  color: rgba(0, 0, 0, 0.6) !important;
}
</style>
